import { isWeb } from "@memorang/helpers/platform";
import { Image } from "expo-image";
import { LinearGradient } from "expo-linear-gradient";
import { useCallback, useState } from "react";
import type { LayoutChangeEvent } from "react-native";
import { StyleSheet, useWindowDimensions } from "react-native";
import Animated, {
	Extrapolation,
	interpolate,
	type SharedValue,
	useAnimatedReaction,
	useAnimatedStyle,
} from "react-native-reanimated";
import { scheduleOnRN } from "react-native-worklets";
import { useAppTheme } from "../../useAppTheme";
import { useDevice } from "../../useDevice";
import Box from "../Box";
import Container from "../Container";
import { supabaseImageUrlParser } from "../CustomImage";
import { type ScreenHeaderAction, ScreenHeaderBar } from "../ScreenHeaderBar";
import { AnimatedHeaderTitle } from "./AnimatedHeaderTitle";
import { COVER_OVERLAY_COLORS, COVER_OVERLAY_LOCATIONS } from "./constants";
import { useAnimatedHeaderScroll } from "./useAnimatedHeaderScroll";

type Props = {
	title: string;
	coverImage?: string;
	leadingAction: ScreenHeaderAction;
	actions?: ScreenHeaderAction[];
	trailingContent?: React.ReactNode;
	renderHero: (ctx: {
		invertColors: boolean;
		onTitleLayout: (e: LayoutChangeEvent) => void;
		titleVariant: "headlineMedium" | "displaySmall";
	}) => React.ReactNode;
	children: React.ReactNode;
	contentPaddingBottom?: number;
	maxWidth?: "xxs" | "xs" | "sm" | "md" | "lg";
	bottomBar?: React.ReactNode;
	/**
	 * Renders children edge to edge instead of inside the `maxWidth` column,
	 * for screens whose content owns full-width bands and constrains itself.
	 */
	fullBleedContent?: boolean;
	/**
	 * On mobile, keeps a solid header bar with the title above the hero instead
	 * of overlaying a transparent header on the cover image.
	 */
	solidHeaderOnMobile?: boolean;
	/** Tints the hero band edge to edge; only applies without a cover image. */
	heroBackgroundColor?: string;
	/**
	 * On mobile, renders a slim banner pinned below the header bar once the
	 * hero scrolls out of view.
	 */
	renderCollapsedHero?: () => React.ReactNode;
};

const CollapsedHeroOverlay = ({
	scrollY,
	heroHeight,
	headerHeight,
	children,
}: {
	scrollY: SharedValue<number>;
	heroHeight: number;
	headerHeight: number;
	children: React.ReactNode;
}) => {
	const [isShown, setIsShown] = useState(false);

	const updateShown = useCallback((next: boolean) => {
		setIsShown(next);
	}, []);

	useAnimatedReaction(
		() => scrollY.value >= heroHeight,
		(shown, prev) => {
			if (shown !== prev) {
				scheduleOnRN(updateShown, shown);
			}
		},
		[updateShown, heroHeight],
	);

	const animatedStyle = useAnimatedStyle(() => {
		const start = Math.max(heroHeight - 40, 0);
		return {
			opacity: interpolate(
				scrollY.value,
				[start, heroHeight],
				[0, 1],
				Extrapolation.CLAMP,
			),
			transform: [
				{
					translateY: interpolate(
						scrollY.value,
						[start, heroHeight],
						[-8, 0],
						Extrapolation.CLAMP,
					),
				},
			],
		};
	}, [heroHeight]);

	return (
		<Animated.View
			pointerEvents={isShown ? "auto" : "none"}
			style={[
				{
					position: "absolute",
					top: headerHeight,
					left: 0,
					right: 0,
					zIndex: 1,
				},
				animatedStyle,
			]}
		>
			{children}
		</Animated.View>
	);
};

export const AnimatedHeaderScreen = ({
	title,
	coverImage,
	leadingAction,
	actions,
	trailingContent,
	renderHero,
	children,
	contentPaddingBottom = 24,
	maxWidth = "sm",
	bottomBar,
	fullBleedContent,
	solidHeaderOnMobile,
	heroBackgroundColor,
	renderCollapsedHero,
}: Props) => {
	const theme = useAppTheme();
	const { isMobile } = useDevice();
	const { width: screenWidth } = useWindowDimensions();
	const heroPaddingTop = isMobile ? 16 : 32;
	const titleVariant = isMobile ? "headlineMedium" : "displaySmall";
	const {
		scrollY,
		scrollHandler,
		headerHeight,
		heroHeight,
		titleScrollThreshold,
		isHeaderSolid,
		onHeaderLayout,
		onHeroLayout,
		onTitleTextLayout,
	} = useAnimatedHeaderScroll();

	const hasCoverImage = !!coverImage;
	const hasStaticHeader =
		!hasCoverImage || Boolean(solidHeaderOnMobile && isMobile);
	const heroMinHeight =
		hasCoverImage && solidHeaderOnMobile && isMobile ? 276 : undefined;
	const isHeaderTransparent =
		hasCoverImage && !hasStaticHeader && !isHeaderSolid;

	const optimizedCoverImageUri = hasCoverImage
		? supabaseImageUrlParser({ src: coverImage, width: screenWidth })
		: undefined;

	const hasCollapsedHero = !!renderCollapsedHero && isMobile && heroHeight > 0;

	const renderHeroBlock = () => (
		<Container maxWidth={maxWidth} autoSize>
			<Box
				paddingTop={
					hasCoverImage && !hasStaticHeader
						? headerHeight + heroPaddingTop
						: heroPaddingTop
				}
				paddingBottom={hasCoverImage ? 16 : 0}
			>
				{renderHero({
					invertColors: hasCoverImage,
					onTitleLayout: onTitleTextLayout,
					titleVariant,
				})}
			</Box>
		</Container>
	);

	const headerBar = (
		<ScreenHeaderBar
			transparent={isHeaderTransparent}
			leadingAction={leadingAction}
			actions={actions}
			trailingContent={trailingContent}
			titleContent={
				<AnimatedHeaderTitle
					title={title}
					scrollY={scrollY}
					titleScrollThreshold={titleScrollThreshold}
					isTransparent={isHeaderTransparent}
					isScrollAware={hasCoverImage && !hasStaticHeader}
				/>
			}
		/>
	);

	return (
		<Box flex={1} height="100%" backgroundColor={theme.colors.background}>
			{hasStaticHeader && <Box onLayout={onHeaderLayout}>{headerBar}</Box>}
			<Animated.ScrollView
				onScroll={scrollHandler}
				scrollEventThrottle={16}
				showsVerticalScrollIndicator={isWeb}
				contentContainerStyle={{
					flexGrow: 1,
					paddingBottom: contentPaddingBottom,
				}}
			>
				{hasCoverImage ? (
					<Box
						position="relative"
						overflow="hidden"
						minHeight={heroMinHeight}
						justifyContent="center"
						onLayout={onHeroLayout}
					>
						<Image
							source={{ uri: optimizedCoverImageUri }}
							style={StyleSheet.absoluteFill}
							contentFit="cover"
						/>
						<LinearGradient
							colors={COVER_OVERLAY_COLORS}
							locations={COVER_OVERLAY_LOCATIONS}
							style={StyleSheet.absoluteFill}
						/>
						{renderHeroBlock()}
					</Box>
				) : (
					<Box backgroundColor={heroBackgroundColor} onLayout={onHeroLayout}>
						{renderHeroBlock()}
					</Box>
				)}
				{fullBleedContent ? (
					children
				) : (
					<Container maxWidth={maxWidth}>{children}</Container>
				)}
			</Animated.ScrollView>
			{hasCollapsedHero && (
				<CollapsedHeroOverlay
					scrollY={scrollY}
					heroHeight={heroHeight}
					headerHeight={headerHeight}
				>
					{renderCollapsedHero()}
				</CollapsedHeroOverlay>
			)}
			{hasCoverImage && !hasStaticHeader && (
				<Box
					position="absolute"
					top={0}
					left={0}
					right={0}
					onLayout={onHeaderLayout}
				>
					{headerBar}
				</Box>
			)}
			{bottomBar}
		</Box>
	);
};
